const RESOLUTION_STEP_KEYS = {
  refund: ["claim_filed", "decision", "refund_issued"],
  store_credit: ["claim_filed", "decision", "store_credit_applied"],
  reship: [
    "claim_filed",
    "decision",
    "reship_created",
    "reship_shipped",
    "reship_delivered",
  ],
  default: ["claim_filed", "decision", "resolved"],
};

const AWAITING_REVIEW_STEPS = ["claim_filed", "decision", "resolved"];

const TIMELINE_ALIASES = {
  claim_filed: ["claim_filed"],
  decision: ["approved", "denied"],
  refund_issued: ["refund_issued"],
  store_credit_applied: ["store_credit_applied"],
  reship_created: ["reship_created"],
  reship_shipped: ["reship_shipped"],
  reship_delivered: ["reship_delivered"],
  resolved: ["closed", "refund_issued", "store_credit_applied", "reship_delivered"],
  denied: ["denied"],
};

export function getEffectiveResolution(claim) {
  if (claim.claim_resolution) {
    return normalizeResolution(claim.claim_resolution);
  }
  if (claim.resolution?.refunds?.length) {
    return "refund";
  }
  if (claim.resolution?.store_credits?.length) {
    return "store_credit";
  }
  if (claim.resolution?.reships?.length) {
    return "reship";
  }
  return normalizeResolution(claim.preferred_resolution) || "default";
}

function normalizeResolution(value) {
  if (!value) {
    return null;
  }

  return String(value).trim().toLowerCase().replace(/\s+/g, "_");
}

export function formatClaimDate(value) {
  if (!value) {
    return null;
  }
  return new Date(value);
}

export function formatMoney(amount, currency = "USD") {
  if (amount == null) {
    return null;
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(amount);
}

function timelineByKey(timeline = []) {
  return timeline.reduce((accumulator, event) => {
    accumulator[event.key] = event.at;
    return accumulator;
  }, {});
}

function getStepDate(stepKey, timelineMap) {
  const aliases = TIMELINE_ALIASES[stepKey] || [stepKey];
  for (const alias of aliases) {
    if (timelineMap[alias]) {
      return timelineMap[alias];
    }
  }
  return null;
}

function getPrimaryReshipShipment(claim) {
  return claim.resolution?.reships?.[0]?.shipments?.[0] || null;
}

function isStepCompleted(stepKey, timelineMap, claim) {
  if (stepKey === "decision") {
    return (
      Boolean(timelineMap.approved) ||
      Boolean(timelineMap.denied) ||
      claim.claim_status === "approved" ||
      claim.claim_status === "closed" ||
      claim.claim_status === "denied"
    );
  }

  if (stepKey === "resolved") {
    return (
      claim.claim_status === "closed" ||
      Boolean(timelineMap.closed) ||
      Boolean(timelineMap.refund_issued) ||
      Boolean(timelineMap.store_credit_applied) ||
      Boolean(timelineMap.reship_delivered)
    );
  }

  if (timelineMap[stepKey]) {
    return true;
  }

  const shipment = getPrimaryReshipShipment(claim);

  switch (stepKey) {
    case "claim_filed":
      return Boolean(timelineMap.claim_filed);
    case "refund_issued":
      return Boolean(timelineMap.refund_issued);
    case "store_credit_applied":
      return Boolean(timelineMap.store_credit_applied);
    case "reship_created":
      return (
        Boolean(timelineMap.reship_created) ||
        Boolean(timelineMap.reship_shipped) ||
        Boolean(timelineMap.reship_delivered) ||
        Boolean(claim.resolution?.reships?.length)
      );
    case "reship_shipped":
      return (
        Boolean(timelineMap.reship_shipped) ||
        Boolean(timelineMap.reship_delivered) ||
        Boolean(shipment?.tracking_number) ||
        Boolean(shipment?.delivered_at)
      );
    case "reship_delivered":
      return (
        Boolean(timelineMap.reship_delivered) || Boolean(shipment?.delivered_at)
      );
    default:
      return false;
  }
}

export function isAwaitingReview(claim) {
  return claim.claim_status === "pending" || claim.claim_status === "new";
}

function getStepState(stepKey, stepIndex, stepKeys, timelineMap, claim) {
  if (claim.claim_status === "denied") {
    if (stepKey === "claim_filed") {
      return "completed";
    }
    if (stepKey === "decision") {
      return "completed";
    }
    return "inactive";
  }

  if (isStepCompleted(stepKey, timelineMap, claim)) {
    return "completed";
  }

  if (isAwaitingReview(claim)) {
    return stepKey === "decision" ? "current" : "inactive";
  }

  const firstIncompleteIndex = stepKeys.findIndex(
    (key) => !isStepCompleted(key, timelineMap, claim),
  );

  if (firstIncompleteIndex === stepIndex) {
    return "current";
  }

  return "inactive";
}

function getStepSubtextKey(stepKey, stepState, claim) {
  if (stepState === "current") {
    return null;
  }

  if (stepState === "completed") {
    return null;
  }

  if (isAwaitingReview(claim) && stepKey === "resolved") {
    return "ifApproved";
  }

  if (
    stepKey === "resolved" ||
    stepKey.startsWith("reship_") ||
    stepKey === "refund_issued" ||
    stepKey === "store_credit_applied"
  ) {
    return "afterDecision";
  }

  return null;
}

function getVisibleStepKeys(resolutionType, claim) {
  if (isAwaitingReview(claim)) {
    return [...AWAITING_REVIEW_STEPS];
  }

  if (claim.claim_status === "denied") {
    return ["claim_filed", "decision"];
  }

  return [...(RESOLUTION_STEP_KEYS[resolutionType] || RESOLUTION_STEP_KEYS.default)];
}

export function shouldShowClaimItems(claim) {
  return Boolean(claim.claim_items?.length);
}

export function shouldShowShippingAddress(claim) {
  return getEffectiveResolution(claim) === "reship";
}

export function getStepperSteps(claim, t) {
  const resolutionType = getEffectiveResolution(claim);
  const stepKeys = getVisibleStepKeys(resolutionType, claim);
  const timelineMap = timelineByKey(claim.timeline);

  return stepKeys.map((stepKey, index) => {
    const state = getStepState(stepKey, index, stepKeys, timelineMap, claim);
    const label = t(`status.stepper.${stepKey}`);

    return {
      key: stepKey,
      label,
      date: getStepDate(stepKey, timelineMap),
      state,
      subtextKey: getStepSubtextKey(stepKey, state, claim),
    };
  });
}

function getHeroKey(claim) {
  if (claim.claim_status === "denied") {
    return "denied";
  }
  if (claim.claim_status === "new" || claim.claim_status === "pending") {
    return claim.claim_status;
  }

  const resolutionType = getEffectiveResolution(claim);
  if (resolutionType === "refund" && claim.resolution?.refunds?.length) {
    return "refundIssued";
  }
  if (
    resolutionType === "store_credit" &&
    claim.resolution?.store_credits?.length
  ) {
    return "storeCreditApplied";
  }
  if (resolutionType === "reship") {
    const reship = claim.resolution?.reships?.[0];
    const shipment = reship?.shipments?.[0];
    if (shipment?.delivered_at) {
      return "reshipDelivered";
    }
    if (shipment?.tracking_number) {
      return "reshipShipped";
    }
    return "reshipPending";
  }

  if (claim.claim_status === "closed") {
    return "closed";
  }

  if (claim.claim_status === "approved") {
    return "approved";
  }

  return claim.claim_status;
}

export function getHeroContent(claim, t) {
  const heroKey = getHeroKey(claim);
  return {
    key: heroKey,
    title: t(`status.hero.${heroKey}.title`),
    description: t(`status.hero.${heroKey}.description`),
    statusLabel: t(`status.hero.${heroKey}.statusLabel`),
  };
}

export function getLastUpdatedAt(claim) {
  const candidates = [
    claim.updated_at,
    claim.closed_at,
    claim.submitted_at,
    claim.created_at,
    ...(claim.timeline || []).map((event) => event.at),
  ].filter(Boolean);

  if (!candidates.length) {
    return null;
  }

  return candidates.reduce((latest, value) => {
    const date = new Date(value);
    return date > latest ? date : latest;
  }, new Date(candidates[0]));
}

export function getCustomerNextSteps(claim, t) {
  if (claim.documents_requested) {
    return {
      title: t("status.nextSteps.documentsTitle"),
      body: t("status.nextSteps.documentsBody"),
      timeframe: isAwaitingReview(claim) ? t("status.nextSteps.timeframe") : null,
      actionRequired: true,
      showNoActionBadge: false,
    };
  }

  if (isAwaitingReview(claim)) {
    return {
      title: t("status.nextSteps.reviewTitle"),
      body: t("status.nextSteps.reviewBody"),
      timeframe: t("status.nextSteps.timeframe"),
      actionRequired: false,
      showNoActionBadge: true,
    };
  }

  if (claim.claim_status === "denied") {
    return {
      title: t("status.nextSteps.deniedTitle"),
      body: t("status.nextSteps.deniedBody"),
      timeframe: null,
      actionRequired: false,
      showNoActionBadge: false,
    };
  }

  return null;
}

function getPendingResolutionSummary(claim, phase) {
  return {
    type: "pending",
    phase,
    preferredResolution: claim.preferred_resolution,
  };
}

function isClaimComplete(claim) {
  return claim.claim_status === "closed" || claim.claim_status === "denied";
}

export function getResolutionSummary(claim) {
  if (isAwaitingReview(claim)) {
    return getPendingResolutionSummary(claim, "review");
  }

  const resolutionType = getEffectiveResolution(claim);
  const currency = claim.order?.currency || "USD";
  const pendingPhase = isClaimComplete(claim) ? "complete" : "resolution";

  if (resolutionType === "refund") {
    const refund = claim.resolution?.refunds?.[0];
    if (!refund) {
      return getPendingResolutionSummary(claim, pendingPhase);
    }

    return {
      type: "refund",
      amount: refund.amount,
      currency: refund.currency || currency,
      issuedAt: refund.refund_date || refund.created_at,
    };
  }

  if (resolutionType === "store_credit") {
    const credit = claim.resolution?.store_credits?.[0];
    if (!credit) {
      return getPendingResolutionSummary(claim, pendingPhase);
    }

    return {
      type: "store_credit",
      amount: credit?.amount,
      currency: credit?.currency || currency,
      discountCode: credit?.discount_code,
      creditType: credit?.credit_type,
      issuedAt: credit?.created_at,
    };
  }

  if (resolutionType === "reship") {
    const reship = claim.resolution?.reships?.[0];
    if (!reship) {
      return getPendingResolutionSummary(claim, pendingPhase);
    }

    const shipment = reship?.shipments?.[0];
    return {
      type: "reship",
      reshipOrderNumber: reship?.reship_order_number,
      reshipShipInsureId: reship?.reship_ship_insure_id,
      shipment,
      createdAt: reship?.created_at,
    };
  }

  return getPendingResolutionSummary(claim, pendingPhase);
}

export function getStatusAnnouncement(claim, hero, t) {
  return t("status.announcement", {
    status: hero.statusLabel,
    claimNumber: claim.claim_number,
  });
}

export function getStatusViewModel(claim, t) {
  const effectiveResolution = getEffectiveResolution(claim);
  const hero = getHeroContent(claim, t);
  const nextSteps = getCustomerNextSteps(claim, t);

  return {
    hero,
    stepperSteps: getStepperSteps(claim, t),
    resolution: getResolutionSummary(claim),
    effectiveResolution,
    showClaimItems: shouldShowClaimItems(claim),
    showShippingAddress: shouldShowShippingAddress(claim),
    lastUpdatedAt: getLastUpdatedAt(claim),
    nextSteps,
    notificationEmail: claim.email_address || null,
    showUploadDocuments: Boolean(claim.documents_requested),
    isAwaitingReview: isAwaitingReview(claim),
    statusAnnouncement: getStatusAnnouncement(claim, hero, t),
  };
}
